import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { PageHeading } from "@via/components/page-heading.tsx";
import IndependentPagination from "@via/components/pagination.tsx";
import SearchBar from "@via/components/search-bar.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import PipelinesTabBar from "@via/pages/pipelines/pipelines-tab-bar.tsx";
import { useProcessCatalog } from "@via/hooks/useProcessCatalog.ts";
import ProcessCardGrid from "@via/pages/pipelines/processes/process-card-grid.tsx";
import { matchesProcessSearch } from "@via/pages/pipelines/processes/process-search.ts";
import ProcessEditorDrawer from "@via/pages/pipelines/processes/process-editor-drawer.tsx";
import ProcessTable from "@via/pages/pipelines/processes/process-table.tsx";
import ViewModeToggle from "@via/pages/pipelines/view-mode-toggle.tsx";
import { useViewMode } from "@via/pages/pipelines/view-mode.ts";
import RunProcessModal from "@via/pages/projects/pipelines/workflow/components/create-edit-process-form/run-process-modal.tsx";
import { ERROR, PAGE_NAMES } from "@via/utils/constants.tsx";
import { EmptyDataIcon } from "@via/utils/custom-icons";
import { UserRole, type ProcessCatalogItem } from "common";
import { useMemo, useState, type JSX } from "react";
import { useDebounceCallback } from "usehooks-ts";

const pageSizes = [10, 20, 100];

export default function ProcessCatalogPage(): JSX.Element {
  const { data, isLoading, isError } = useProcessCatalog();
  const { user } = useAuth();
  const isGuest = user?.userRole === UserRole.Guest;
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const debouncedSearch = useDebounceCallback((query: string) => {
    setSearchQuery(query);
    setPage(0);
  }, 300);
  const [viewMode, setViewMode] = useViewMode("via.processes.view");
  const [runItem, setRunItem] = useState<ProcessCatalogItem | null>(null);
  // Tracked by id, not by item, so the drawer can switch to a copy the catalog
  // has not refetched yet.
  const [openId, setOpenId] = useState<number | null>(null);

  const catalog = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(
    () => catalog.filter(item => matchesProcessSearch(item, searchQuery)),
    [catalog, searchQuery]
  );
  // The whole catalog is already loaded, so paging happens here rather than
  // on the server, and search still covers every process.
  const pageItems = useMemo(
    () => filtered.slice(page * pageSize, (page + 1) * pageSize),
    [filtered, page, pageSize]
  );

  const renderBody = (): JSX.Element | null => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      );
    }
    if (filtered.length === 0) {
      return (
        <div className="via-generic-error">
          <EmptyDataIcon />
          <p>
            {catalog.length === 0
              ? "No processes found."
              : "No processes match your search."}
          </p>
        </div>
      );
    }
    const View = viewMode === "cards" ? ProcessCardGrid : ProcessTable;
    return (
      <>
        <View
          items={pageItems}
          isGuest={isGuest}
          onRun={setRunItem}
          onOpen={item => setOpenId(item.id)}
        />
        <IndependentPagination
          data={filtered}
          page={page}
          pageSizes={pageSizes}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={size => {
            setPageSize(size);
            setPage(0);
          }}
          total={filtered.length}
          itemLabel={{ singular: "process", plural: "processes" }}
        />
      </>
    );
  };

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        <PageHeading title={PAGE_NAMES.PIPELINES} />
      </div>
      <PipelinesTabBar active="processes" />
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SearchBar
            placeholder="Search processes by name, description, tag or owner"
            onSearchChange={debouncedSearch}
          />
        </div>
        <ViewModeToggle mode={viewMode} onChange={setViewMode} />
      </div>
      {renderBody()}
      <ProcessEditorDrawer
        itemId={openId}
        open={openId !== null}
        setOpen={isOpen => {
          if (!isOpen) setOpenId(null);
        }}
        onOpenCopy={setOpenId}
      />
      {runItem && (
        <RunProcessModal
          open
          setOpen={() => setRunItem(null)}
          processId={runItem.id}
        />
      )}
    </main>
  );
}
